/**
 * Pure table helpers — column typing, sorting, filtering, CSV.
 *
 * Kept free of React so the behaviour that is easy to get subtly wrong (numeric
 * vs lexical sort, CSV quoting, blank handling) is unit-testable without a DOM.
 */

import type { ParsedArtifactTable } from "./artifact-utils.js";

export type ColumnAlignment = "left" | "right";

export interface TableColumnMeta {
  readonly key: string;
  /** Right-align numbers; a column of figures is unreadable ragged-left. */
  readonly align: ColumnAlignment;
  /** True when every present value is a number — drives alignment AND sort order. */
  readonly numeric: boolean;
}

export type SortDirection = "asc" | "desc";

export interface TableSort {
  readonly column: string;
  readonly direction: SortDirection;
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

/**
 * Decide per-column type from the data.
 *
 * A column counts as numeric only if EVERY non-blank value is a number — one
 * "N/A" in a price column makes it text, because sorting it numerically would
 * have to invent a position for that row.
 */
export function describeColumns(table: ParsedArtifactTable): TableColumnMeta[] {
  return table.columns.map((key) => {
    let sawValue = false;
    let allNumeric = true;
    for (const row of table.rows) {
      const value = row[key];
      if (isBlank(value)) continue;
      sawValue = true;
      if (typeof value !== "number") {
        allNumeric = false;
        break;
      }
    }
    const numeric = sawValue && allNumeric;
    return { key, numeric, align: numeric ? "right" : "left" };
  });
}

/**
 * Compare two cell values for sorting.
 *
 * Blanks always sink to the bottom regardless of direction — a column of prices
 * sorted descending should start with the highest price, not with the rows that
 * have no price. Numbers compare numerically; everything else compares as
 * locale-aware text with numeric collation, so "Item 2" sorts before "Item 10".
 */
export function compareCellValues(a: unknown, b: unknown, numeric: boolean): number {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;

  if (numeric && typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  if (typeof a === "boolean" && typeof b === "boolean") {
    return Number(a) - Number(b);
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/** Sort rows by one column. Returns a new array; the input is never mutated. */
export function sortTableRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  sort: TableSort | null,
  columns: readonly TableColumnMeta[],
): Array<Record<string, unknown>> {
  if (!sort) return [...rows];
  const meta = columns.find((column) => column.key === sort.column);
  const factor = sort.direction === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const result = compareCellValues(a[sort.column], b[sort.column], meta?.numeric === true);
    // Blanks sink in BOTH directions, so their comparison is not flipped.
    if (result === 0) return 0;
    const aBlank = isBlank(a[sort.column]);
    const bBlank = isBlank(b[sort.column]);
    if (aBlank || bBlank) return result;
    return result * factor;
  });
}

/**
 * Filter rows by a free-text query across every column.
 *
 * Case-insensitive substring across the whole row, which is what someone typing
 * into a box above a table expects — not a per-column query language.
 */
export function filterTableRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  columns: readonly string[],
  query: string,
): Array<Record<string, unknown>> {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...rows];
  return rows.filter((row) =>
    columns.some((column) => {
      const value = row[column];
      if (isBlank(value)) return false;
      const text = typeof value === "object" ? JSON.stringify(value) : String(value);
      return text.toLowerCase().includes(needle);
    }),
  );
}

/** Escape one CSV field per RFC 4180. */
function csvField(value: unknown): string {
  if (isBlank(value)) return "";
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  // Quote when the field contains a delimiter, a quote, or any newline; double
  // internal quotes. A leading `=`/`+`/`-`/`@` is prefixed with a single quote so
  // spreadsheet software treats it as text — an unescaped `=cmd|...` in a CSV is
  // a formula-injection payload, and these tables come from model output.
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/**
 * Render a table as CSV.
 *
 * CRLF line endings and a UTF-8 BOM, because the overwhelmingly likely
 * destination is Excel: without the BOM it mangles any non-ASCII character in a
 * company name, and without CRLF older versions run rows together.
 */
export function toCsv(
  columns: readonly string[],
  rows: ReadonlyArray<Record<string, unknown>>,
): string {
  const lines = [columns.map(csvField).join(",")];
  for (const row of rows) {
    lines.push(columns.map((column) => csvField(row[column])).join(","));
  }
  return `﻿${lines.join("\r\n")}\r\n`;
}

/** Next sort state for a header click: asc → desc → unsorted. */
export function nextSortState(current: TableSort | null, column: string): TableSort | null {
  if (current?.column !== column) return { column, direction: "asc" };
  if (current.direction === "asc") return { column, direction: "desc" };
  return null;
}
